import React from "react"
import type { AiSubject } from "../Session/plan-lesson/ai-lesson-planning/aiLessonPlanningTypes"
import { SUBJECT_META } from "./learnerReportModel"

export const ChevronLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" />
  </svg>
)

export const ChevronRight = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#8C93A3" strokeWidth="2" aria-hidden="true" style={{ alignSelf: "center", flex: "none" }}>
    <path d="M9 6l6 6-6 6" />
  </svg>
)

export const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 11a8 8 0 1 0-2.3 5.7" />
    <path d="M20 4v7h-7" />
  </svg>
)

export const WarningIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8A5300" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l10 18H2z" />
    <path d="M12 10v5M12 18v.01" />
  </svg>
)

export const ExpandButton = ({
  open,
  onClick,
  label,
}: {
  open: boolean
  onClick: () => void
  label: string
}) => (
  <button
    type="button"
    className="sr-icon-btn"
    onClick={onClick}
    aria-expanded={open}
    aria-label={label}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d={open ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"} />
    </svg>
  </button>
)

export const SubjectChip = ({
  subject,
  small,
}: {
  subject: AiSubject
  small?: boolean
}) => {
  const meta = SUBJECT_META[subject]
  return (
    <span
      className={small ? "sr-subj-chip sr-subj-chip-sm" : "sr-subj-chip"}
      style={{ background: meta.bg, color: meta.fg }}
    >
      {meta.label}
    </span>
  )
}

export function PillGroup<T extends string>({
  options,
  value,
  onChange,
  label,
  size = "md",
}: {
  options: Array<{ key: T; label: string }>
  value: T
  onChange: (key: T) => void
  label: string
  size?: "md" | "sm"
}) {
  return (
    <div role="group" aria-label={label} className="sr-pill-group sr-no-print">
      {options.map(o => (
        <button
          key={o.key}
          type="button"
          className={size === "sm" ? "sr-pill" : "sr-pill sr-pill-lg"}
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export type SubjectFilter = "both" | AiSubject

export const SUBJECT_FILTERS: Array<{ key: SubjectFilter; label: string }> = [
  { key: "both", label: "Both" },
  { key: "math", label: "Maths" },
  { key: "english", label: "English" },
]

export const ScoreLegend = () => (
  <div className="sr-legend-row">
    <span className="sr-legend-item"><span className="sr-legend-swatch" style={{ background: "#D7354B" }} />Weak 0–3</span>
    <span className="sr-legend-item"><span className="sr-legend-swatch" style={{ background: "#E0A040" }} />In progress 4–6</span>
    <span className="sr-legend-item"><span className="sr-legend-swatch" style={{ background: "#3B5BCC" }} />Good 7–8.9</span>
    <span className="sr-legend-item"><span className="sr-legend-swatch" style={{ background: "#2E9D5E" }} />Passed 9+</span>
  </div>
)

export const WhenBadge = ({ when }: { when: string }) => {
  const style: React.CSSProperties =
    when === "NOW" || when === "REVISIT"
      ? { background: "#1F2F6B", color: "#FFFFFF" }
      : when === "NEXT"
      ? { background: "#E8ECF7", color: "#1F2F6B" }
      : { background: "#EEF0F5", color: "#4A5163" }
  return (
    <span className="sr-when" style={style}>
      {when}
    </span>
  )
}

export const BasketBadge = ({ basket }: { basket: string }) => (
  <span
    className="sr-basket-badge"
    style={
      basket === "Relearn"
        ? { background: "#FDEEDB", color: "#8A5300" }
        : basket === "Preference"
        ? { background: "#FBE4EA", color: "#9C2A51" }
        : { background: "#E4EAFB", color: "#2E4AA6" }
    }
  >
    {basket}
  </span>
)

export const EmptyNote = ({ children }: { children: React.ReactNode }) => (
  <div className="sr-empty">{children}</div>
)
